/*
 * SoleThreads — Shop The Look (sections/solethreads-shop-the-look.liquid)
 *
 * <solethreads-shop-the-look> wraps one section's heading, arrows, reel track and popup.
 *   - Arrows scroll the native, swipeable track by one card; they show only when
 *     the reels overflow, and disable at either end.
 *   - Reel videos have preload="none" and play only while at least half on screen,
 *     pausing when scrolled away. Without autoplay, or with prefers-reduced-motion,
 *     they play on hover/keyboard focus instead.
 *   - Clicking a reel ([data-stl-open]) opens the shoppable popup (a native <dialog>)
 *     with that reel's video and product, read from the card's [data-stl-reel] JSON.
 *     The product info on a card is a plain link and is never intercepted.
 *
 * Popup: option buttons resolve to the product's real variant (exact match on every
 * option); Add to cart posts that variant id to cart/add.js and then refreshes and
 * opens Glozin's cart drawer the same way its <product-form> does (assets/theme.js).
 * Only the popup reel plays while it is open; the card reels resume when it closes.
 */
(() => {
  const TAG = "solethreads-shop-the-look";
  if (customElements.get(TAG)) return;

  const OPEN_CLASS = "stl-modal-open";
  const TEXT = {
    add: "Add to cart",
    adding: "Adding…",
    soldOut: "Sold out",
    unavailable: "Unavailable",
    count: (index, total) => `${String(index).padStart(2, "0")} / ${String(total).padStart(2, "0")}`,
    cartError: (reason) => `Couldn't add to your cart${reason ? `: ${reason}` : ". Please try again."}`,
    salePrice: "Sale price",
    regularPrice: "Regular price",
  };

  const routesRoot = () => window.Shopify?.routes?.root || "/";

  const readJSON = (element) => {
    try {
      return JSON.parse(element.textContent);
    } catch (error) {
      console.warn("Shop the look: invalid reel data.", error);
      return null;
    }
  };

  const isDefaultOnly = (options) =>
    options.length === 1 && options[0].values.length === 1 && options[0].values[0] === "Default Title";

  class SolethreadsShopTheLook extends HTMLElement {
    connectedCallback() {
      if (this.track) {
        // Moved within the page (e.g. sections reordered in the theme editor).
        this.observe();
        return;
      }
      this.track = this.querySelector("[data-stl-track]");
      if (!this.track) return;

      this.prev = this.querySelector("[data-stl-prev]");
      this.next = this.querySelector("[data-stl-next]");
      this.videos = Array.from(this.track.querySelectorAll("[data-stl-video]"));
      this.visible = new Map();
      this.reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
      this.updateNav = this.updateNav.bind(this);

      this.prev?.addEventListener("click", () => this.go(-1));
      this.next?.addEventListener("click", () => this.go(1));
      this.track.addEventListener("scroll", this.updateNav, { passive: true });

      this.videos.forEach((video) => {
        const card = video.closest(".stl-card");
        const start = () => {
          if (!this.modalOpen && !this.autoplays(video)) this.play(video);
        };
        const stop = () => {
          if (!this.autoplays(video)) video.pause();
        };
        card.addEventListener("mouseenter", start);
        card.addEventListener("mouseleave", stop);
        card.addEventListener("focusin", start);
        card.addEventListener("focusout", stop);
      });

      this.setupModal();
      this.observe();
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
      this.videoObserver?.disconnect();
      // Removed while open (theme editor re-render): undo the page-level scroll lock.
      if (this.modalOpen) {
        this.modalOpen = false;
        document.documentElement.classList.remove(OPEN_CLASS);
        this.stopModalVideo();
      }
    }

    observe() {
      if ("ResizeObserver" in window) {
        this.resizeObserver ||= new ResizeObserver(this.updateNav);
        this.resizeObserver.observe(this.track);
      }
      this.updateNav();

      if ("IntersectionObserver" in window) {
        this.videoObserver ||= new IntersectionObserver(
          (entries) => entries.forEach((entry) => this.toggleVideo(entry.target, entry.intersectionRatio >= 0.5)),
          { threshold: [0, 0.5] }
        );
        this.videos.forEach((video) => this.videoObserver.observe(video));
      }
    }

    autoplays(video) {
      return video.hasAttribute("data-autoplay") && !this.reduceMotion.matches;
    }

    // Browsers may refuse playback (e.g. an unmuted reel without a user gesture); the poster stays.
    play(video) {
      const playing = video.play();
      if (playing) playing.catch(() => {});
    }

    toggleVideo(video, visible) {
      this.visible.set(video, visible);
      if (visible && !this.modalOpen && this.autoplays(video)) this.play(video);
      else if (!visible) video.pause();
    }

    step() {
      const card = this.track.querySelector(".stl-card");
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      return card ? card.offsetWidth + gap : this.track.clientWidth;
    }

    go(direction) {
      this.track.scrollBy({
        left: direction * this.step(),
        behavior: this.reduceMotion.matches ? "auto" : "smooth",
      });
    }

    updateNav() {
      const max = this.track.scrollWidth - this.track.clientWidth;
      this.toggleAttribute("data-scrollable", max > 1);
      if (this.prev) this.prev.disabled = this.track.scrollLeft <= 1;
      if (this.next) this.next.disabled = this.track.scrollLeft >= max - 1;
    }

    /* ---------- Popup ---------- */

    setupModal() {
      this.modal = this.querySelector("[data-stl-modal]");
      if (!this.modal || typeof this.modal.showModal !== "function") return;

      const find = (selector) => this.modal.querySelector(selector);
      this.m = {
        close: find("[data-stl-close]"),
        video: find("[data-stl-modal-video]"),
        poster: find("[data-stl-modal-poster]"),
        steps: this.modal.querySelectorAll("[data-stl-step]"),
        count: find("[data-stl-modal-count]"),
        image: find("[data-stl-modal-image]"),
        title: find("[data-stl-modal-title]"),
        price: find("[data-stl-modal-price]"),
        desc: find("[data-stl-modal-desc]"),
        options: find("[data-stl-modal-options]"),
        note: find("[data-stl-modal-note]"),
        add: find("[data-stl-modal-add]"),
        link: find("[data-stl-modal-link]"),
      };

      // One entry per reel that has a product, in card order.
      this.reels = Array.from(this.track.querySelectorAll("[data-stl-open]"))
        .map((button) => {
          const script = button.closest(".stl-card")?.querySelector("[data-stl-reel]");
          const data = script && readJSON(script);
          return data ? { button, data } : null;
        })
        .filter(Boolean);
      if (!this.reels.length) return;

      this.track.addEventListener("click", (event) => {
        const button = event.target.closest("[data-stl-open]");
        if (!button) return;
        const index = this.reels.findIndex((reel) => reel.button === button);
        if (index !== -1) this.openModal(index, button);
      });

      this.m.close.addEventListener("click", () => this.modal.close());
      // The panel fills the dialog, so a click on the dialog itself is the backdrop.
      this.modal.addEventListener("click", (event) => {
        if (event.target === this.modal) this.modal.close();
      });
      this.modal.addEventListener("close", () => this.onModalClose());
      this.m.steps.forEach((button) =>
        button.addEventListener("click", () => this.showReel(this.index + Number(button.dataset.stlStep)))
      );
      this.m.options.addEventListener("click", (event) => {
        const button = event.target.closest("[data-option-index]");
        if (button) this.chooseOption(Number(button.dataset.optionIndex), button.dataset.value);
      });
      this.m.add.addEventListener("click", () => this.addToCart());
    }

    openModal(index, opener) {
      this.opener = opener;
      this.modalOpen = true;
      this.videos.forEach((video) => video.pause());

      const scrollbar = window.innerWidth - document.documentElement.clientWidth;
      document.documentElement.style.setProperty("--stl-scrollbar", `${Math.max(scrollbar, 0)}px`);
      document.documentElement.classList.add(OPEN_CLASS);

      this.modal.showModal();
      this.showReel(index);
      this.m.close.focus();
    }

    onModalClose() {
      this.modalOpen = false;
      this.stopModalVideo();
      document.documentElement.classList.remove(OPEN_CLASS);
      document.documentElement.style.removeProperty("--stl-scrollbar");
      // Card reels that are on screen carry on.
      this.videos.forEach((video) => {
        if (this.visible.get(video) && this.autoplays(video)) this.play(video);
      });
      this.opener?.focus({ preventScroll: true });
    }

    // Pauses the popup reel and lets the browser drop its download.
    stopModalVideo() {
      const video = this.m?.video;
      if (!video) return;
      video.pause();
      if (video.hasAttribute("src")) {
        video.removeAttribute("src");
        video.load();
      }
    }

    showReel(index) {
      const total = this.reels.length;
      this.index = ((index % total) + total) % total;
      const reel = this.reels[this.index].data;
      this.reel = reel;

      this.m.count.textContent = total > 1 ? TEXT.count(this.index + 1, total) : "";
      this.m.steps.forEach((button) => {
        button.hidden = total < 2;
      });

      // Reel: the block's video, or its poster when there is none.
      const { video, poster } = this.m;
      this.stopModalVideo();
      if (reel.video) {
        if (reel.poster) video.poster = reel.poster;
        else video.removeAttribute("poster");
        video.src = reel.video;
        video.hidden = false;
        poster.hidden = true;
        this.play(video);
      } else {
        video.hidden = true;
        poster.hidden = !reel.poster;
        if (reel.poster) poster.src = reel.poster;
      }

      this.m.title.textContent = reel.title;
      this.m.desc.textContent = reel.description || "";
      this.setNote("");

      // Start from the block's variant (or the first available one).
      const start =
        reel.variants.find((variant) => variant.id === reel.variantId) ||
        reel.variants.find((variant) => variant.available) ||
        reel.variants[0];
      this.choices = start ? [...start.options] : reel.options.map(() => null);

      this.renderOptions();
      this.updateVariant();
    }

    renderOptions() {
      const { options } = this.reel;
      if (isDefaultOnly(options)) {
        this.m.options.replaceChildren();
        return;
      }
      const groups = options.map((option, index) => {
        // A colour option shows each colour's own variant photo, when the variants have photos.
        const images = /colou?r/i.test(option.name) ? this.colourImages(index) : null;
        const fieldset = document.createElement("fieldset");
        fieldset.className = `stl-modal__fieldset${images ? " stl-modal__fieldset--colour" : ""}`;
        const legend = document.createElement("legend");
        legend.className = "stl-modal__legend";
        legend.append(option.name, document.createElement("span"));
        const values = document.createElement("div");
        values.className = images ? "stl-modal__swatches" : "stl-modal__values";
        option.values.forEach((value) => {
          const button = document.createElement("button");
          button.type = "button";
          button.className = images ? "stl-modal__swatch" : "stl-modal__value";
          button.dataset.optionIndex = index;
          button.dataset.value = value;
          if (images) {
            const media = document.createElement("span");
            media.className = "stl-modal__swatch-media";
            if (images.has(value)) {
              const image = document.createElement("img");
              image.src = images.get(value);
              image.alt = "";
              image.width = 400;
              image.height = 400;
              image.loading = "lazy";
              media.append(image);
            }
            const name = document.createElement("span");
            name.className = "stl-modal__swatch-name";
            name.textContent = value;
            button.append(media, name);
          } else {
            button.textContent = value;
          }
          values.append(button);
        });
        fieldset.append(legend, values);
        return fieldset;
      });
      this.m.options.replaceChildren(...groups);
    }

    // Colour value → that colour's variant photo (the first variant of the colour that has one),
    // or null when no variant of this product has a photo.
    colourImages(index) {
      const images = new Map();
      this.reel.variants.forEach((variant) => {
        const value = variant.options[index];
        if (variant.image && !images.has(value)) images.set(value, variant.image);
      });
      return images.size ? images : null;
    }

    chooseOption(index, value) {
      this.choices[index] = value;
      this.setNote("");
      this.updateVariant();
    }

    // The real variant for the chosen values, or null if that combination doesn't exist.
    currentVariant() {
      return (
        this.reel.variants.find((variant) => variant.options.every((value, i) => value === this.choices[i])) || null
      );
    }

    updateVariant() {
      const reel = this.reel;
      const variant = this.currentVariant();
      this.variant = variant;

      // Option states: pressed, and struck through when no available variant has that value
      // together with the other chosen values.
      this.m.options.querySelectorAll("[data-option-index]").forEach((button) => {
        const index = Number(button.dataset.optionIndex);
        const value = button.dataset.value;
        const pressed = this.choices[index] === value;
        button.setAttribute("aria-pressed", String(pressed));
        const available = reel.variants.some(
          (item) =>
            item.available &&
            item.options[index] === value &&
            item.options.every((other, i) => i === index || other === this.choices[i])
        );
        button.toggleAttribute("data-unavailable", !available);
        button.setAttribute("aria-label", available ? value : `${value}, ${TEXT.unavailable}`);
      });
      this.m.options.querySelectorAll(".stl-modal__fieldset").forEach((fieldset, index) => {
        fieldset.querySelector(".stl-modal__legend span").textContent = this.choices[index] ?? "";
      });

      // Price (formatted by Liquid with the shop's money format).
      const shown = variant || reel.variants.find((item) => item.id === reel.variantId) || reel.variants[0];
      this.renderPrice(shown);

      const image = variant?.image || reel.image;
      this.m.image.hidden = !image;
      if (image) this.m.image.src = image;

      this.m.link.href = variant ? variant.url : reel.url;

      const canAdd = !!variant && variant.available;
      this.m.add.disabled = !canAdd;
      this.m.add.textContent = !variant ? TEXT.unavailable : variant.available ? TEXT.add : TEXT.soldOut;
    }

    renderPrice(variant) {
      const price = this.m.price;
      price.replaceChildren();
      if (!variant) return;
      const span = (className, html) => {
        const element = document.createElement(className === "was" ? "s" : "span");
        if (className && className !== "was") element.className = className;
        element.innerHTML = html;
        return element;
      };
      const hidden = (text) => {
        const element = document.createElement("span");
        element.className = "visually-hidden";
        element.textContent = text;
        return element;
      };
      if (variant.compare) {
        price.append(hidden(TEXT.salePrice), span("", variant.price), hidden(TEXT.regularPrice), span("was", variant.compare));
        if (variant.saving > 0) price.append(span("stl-modal__sale", `-${variant.saving}%`));
      } else {
        price.append(span("", variant.price));
      }
    }

    setNote(message) {
      this.m.note.textContent = message;
    }

    /* ---------- Popup: add to cart (Glozin's cart drawer) ---------- */

    async addToCart() {
      const button = this.m.add;
      const variant = this.variant;
      if (!variant || !variant.available || button.getAttribute("aria-busy") === "true") return;

      const cart = document.querySelector("cart-notification") || document.querySelector("cart-drawer");
      const body = { items: [{ id: variant.id, quantity: 1 }] };
      if (typeof cart?.getSectionsToRender === "function") {
        body.sections = cart.getSectionsToRender().map((section) => section.id);
        body.sections_url = window.location.pathname;
      }

      this.setNote("");
      button.disabled = true;
      button.setAttribute("aria-busy", "true");
      button.textContent = TEXT.adding;

      try {
        const response = await fetch(`${routesRoot()}cart/add.js`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            "X-Requested-With": "XMLHttpRequest",
          },
          body: JSON.stringify(body),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok || data.status) {
          this.setNote(TEXT.cartError(data.description || data.message));
          return;
        }
        if (!cart) {
          window.location.href = `${routesRoot()}cart`;
          return;
        }
        this.modal.close();
        this.openCartDrawer(cart, data);
      } catch (error) {
        console.warn("Shop the look: add to cart failed.", error);
        this.setNote(TEXT.cartError(""));
      } finally {
        button.removeAttribute("aria-busy");
        this.updateVariant();
      }
    }

    // Same steps as Glozin's <product-form> after a successful add.
    openCartDrawer(cart, data) {
      this.updateCartCount();
      if (typeof publish === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: "solethreads-shop-the-look", cartData: data });
      }
      if (data.sections) {
        cart.getSectionsToRender().forEach((section) => {
          const target = document.getElementById(section.id);
          const markup = data.sections[section.id];
          if (!target || !markup) return;
          const form = new DOMParser().parseFromString(markup, "text/html").querySelector("#minicart-form");
          if (form) target.innerHTML = form.innerHTML;
        });
      }
      if (typeof cart.cartAction === "function") cart.cartAction();
      cart.classList.remove("is-empty");
      if (typeof cart.setActiveElement === "function" && this.opener) cart.setActiveElement(this.opener);
      setTimeout(() => {
        if (typeof showMinicartAddSuccessMessage === "function") showMinicartAddSuccessMessage(cart);
        if (typeof cart.open === "function") cart.open();
      }, 300);
    }

    updateCartCount() {
      fetch(`${routesRoot()}cart.js`)
        .then((response) => response.json())
        .then((cartData) => {
          if (cartData.item_count === undefined) return;
          document.querySelectorAll(".cart-count").forEach((element) => {
            element.innerHTML = element.classList.contains("cart-count-drawer")
              ? `(${cartData.item_count})`
              : cartData.item_count > 100
                ? "~"
                : cartData.item_count;
          });
          document.querySelector("header-total-price")?.updateTotal?.(cartData);
          document.querySelector("free-ship-progress-bar")?.init?.(cartData.items_subtotal_price);
        })
        .catch((error) => console.warn("Shop the look: cart count update failed.", error));
    }
  }

  customElements.define(TAG, SolethreadsShopTheLook);
})();
